import React from 'react';
import { SegmentedControl } from '@components/primitives/SegmentedControl';
import { useAppointments } from '@features/appointments';
import { ListIcon } from '@jotpsych/ui/icons/ListIcon';
import { MonthIcon } from '@jotpsych/ui/icons/MonthIcon';
import { WeekIcon } from '@jotpsych/ui/icons/WeekIcon';
import { Box, FormControl, MenuItem, Select, type SelectChangeEvent } from '@mui/material';

interface ViewModeSelectorProps {
  variant?: 'segmented' | 'dropdown';
}

/**
 * Self-contained view mode selector component
 * Supports both segmented control (desktop) and dropdown (mobile) variants
 */
// Type guard for valid view modes
const isValidViewMode = (value: string): value is 'day' | 'week' | 'month' => {
  return value === 'day' || value === 'week' || value === 'month';
};

export const ViewModeSelector: React.FC<ViewModeSelectorProps> = ({ variant = 'segmented' }) => {
  const { viewMode, setViewMode } = useAppointments();

  const options = [
    { value: 'day', label: 'Day', icon: <ListIcon fontSize="small" /> },
    { value: 'week', label: 'Week', icon: <WeekIcon fontSize="small" /> },
    { value: 'month', label: 'Month', icon: <MonthIcon fontSize="small" /> },
  ];

  const handleDropdownChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    if (isValidViewMode(value)) {
      setViewMode(value);
    }
  };

  const handleSegmentedChange = (value: string) => {
    if (isValidViewMode(value)) {
      setViewMode(value);
    }
  };

  if (variant === 'dropdown') {
    const selectedOption = options.find(opt => opt.value === viewMode);

    return (
      <FormControl fullWidth size="small">
        <Select
          value={viewMode}
          onChange={handleDropdownChange}
          displayEmpty
          renderValue={() => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {selectedOption?.icon}
              <span>{selectedOption?.label}</span>
            </Box>
          )}
          sx={{
            // Match outlined button styling
            color: '#535976',
            backgroundColor: 'white',
            border: '1px solid rgba(0, 13, 77, 0.45)',
            boxShadow: '0 2px 4px -2px rgba(0, 0, 0, 0.08), 0 4px 8px -2px rgba(0, 0, 0, 0.04)', // Subtle shadow from Button.styles

            // Fix borderRadius - apply to fieldset and root
            '& fieldset': {
              borderRadius: '8px',
            },
            borderRadius: '8px',

            // Font styling to match buttons
            fontWeight: 500,
            fontSize: '0.9375rem',
            lineHeight: 1.75,

            // Remove default MUI Select border
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },

            // Padding to match buttons
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1,
            },

            // Icon styling
            '& .MuiSvgIcon-root': {
              color: '#535976',
            },

            // Hover state matching buttons
            '&:hover': {
              borderColor: '#535976',
              backgroundColor: 'rgba(83, 89, 118, 0.04)',
              boxShadow: '0 2px 4px -2px rgba(0, 0, 0, 0.12), 0 4px 8px -2px rgba(0, 0, 0, 0.08)', // Slightly more defined on hover
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            },

            // Focus state
            '&.Mui-focused': {
              borderColor: '#535976',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            },
          }}
        >
          {options.map(option => (
            <MenuItem key={option.value} value={option.value}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#535976' }}>
                {option.icon}
                {option.label}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return (
    <SegmentedControl
      value={viewMode}
      onChange={handleSegmentedChange}
      options={options}
      size="medium"
    />
  );
};

export default ViewModeSelector;
